import { useState } from 'react'
import { useAppStore } from '../../store/app'
import { sendSetCurrentOrganization, wsRequest } from '../../api/ws'
import './HouseholdPickerSheet.css'

// The 'accept' mode is gone. Accepting an invitation used to mean pasting
// a token here, so it belonged next to household switching; now
// invitations are a list the server hands you, with their own sheet.
type Mode = 'list' | 'create'

export function HouseholdPickerSheet({ initialMode }: { initialMode?: Mode }) {
  const orgs = useAppStore((s) => s.availableOrganizations)
  const ownedOrgCap = useAppStore((s) => s.ownedOrgCap)
  const myInvites = useAppStore((s) => s.myInvites)
  const pushSheet = useAppStore((s) => s.pushSheet)
  const popSheet = useAppStore((s) => s.popSheet)

  const ownedCount = orgs.filter((o) => o.role === 'owner').length
  const atCap = ownedOrgCap > 0 && ownedCount >= ownedOrgCap
  const [mode, setMode] = useState<Mode>(initialMode ?? 'list')
  const [name, setName] = useState('')
  const [submitting, setSubmitting] = useState(false)
  const [error, setError] = useState<string | null>(null)

  function select(id: string) {
    sendSetCurrentOrganization(id)
    popSheet()
  }

  async function createOrg() {
    const trimmed = name.trim()
    if (!trimmed || submitting) return
    setSubmitting(true)
    setError(null)
    try {
      const resp = await wsRequest<{
        organization?: { id: string }
        error?: string
      }>(
        'create_organization',
        { name: trimmed },
        'organization_created',
        { kind: 'organization', action: 'create' },
      )
      if (resp.error || !resp.organization?.id) {
        setError(resp.error ?? 'failed to create household')
        setSubmitting(false)
        return
      }
      // Server made the caller an owner in the same tx. Switch to it
      // immediately so the rest of the app re-scopes to the new household.
      sendSetCurrentOrganization(resp.organization.id)
      popSheet()
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err))
      setSubmitting(false)
    }
  }

  if (mode === 'create') {
    return (
      <div className="household-picker">
        <div className="household-picker__form">
          <label className="household-picker__label">Household name</label>
          <input
            className="household-picker__input"
            autoFocus
            value={name}
            placeholder="e.g. The Smiths"
            onChange={(e) => setName(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') void createOrg()
            }}
          />
          {error && <div className="household-picker__error">{error}</div>}
          <button
            className="household-picker__primary"
            disabled={submitting || name.trim() === ''}
            onClick={() => void createOrg()}
          >
            {submitting ? 'Creating…' : 'Create & Switch'}
          </button>
          <button
            className="household-picker__secondary"
            disabled={submitting}
            onClick={() => {
              setError(null)
              popSheet()
            }}
          >
            Cancel
          </button>
        </div>
        <div className="household-picker__footer">
          You'll be the owner of this new household. Members can be added
          via Invite from the Household tab.
        </div>
      </div>
    )
  }

  return (
    <div className="household-picker">
      <div className="household-picker__group">
        {orgs.map((org) => (
          <button
            key={org.id}
            className="household-picker__row"
            onClick={() => select(org.id)}
            disabled={org.is_current}
          >
            <div className="household-picker__info">
              <div className="household-picker__name">{org.name}</div>
              {org.role !== 'member' && (
                <div className="household-picker__role">{org.role}</div>
              )}
            </div>
            {org.is_current && <div className="household-picker__check">✓</div>}
          </button>
        ))}
      </div>
      <button
        className={`household-picker__create${atCap ? ' household-picker__create--capped' : ''}`}
        disabled={atCap}
        onClick={() => {
          setName('')
          setError(null)
          setMode('create')
        }}
      >
        {atCap
          ? `+ Create new household — limit of ${ownedOrgCap} reached`
          : '+ Create new household'}
      </button>
      {myInvites.length > 0 && (
        <button
          className="household-picker__create"
          onClick={() => {
            popSheet()
            pushSheet({ kind: 'invitations' })
          }}
        >
          {myInvites.length === 1
            ? '1 invitation waiting'
            : `${myInvites.length} invitations waiting`}
        </button>
      )}
      <div className="household-picker__footer">
        The current household scopes the agents, positions, and rules shown
        throughout the app.
      </div>
    </div>
  )
}
